export const MAX_IMAGE_UPLOAD_BYTES = 200_000;

export class ImageCompressionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImageCompressionError';
  }
}

const SUPPORTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MIME_BY_EXTENSION: Record<string, string> = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp' };
const QUALITIES = [0.9, 0.8, 0.68, 0.55, 0.42];

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      if (image.naturalWidth > 0 && image.naturalHeight > 0) resolve(image);
      else reject(new ImageCompressionError('无法读取图片尺寸，请重新选择图片。'));
    };
    image.onerror = () => reject(new ImageCompressionError('图片无法读取，请选择有效的 JPG、PNG 或 WebP 图片。'));
    image.src = url;
  });
}

function encode(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob(resolve, type, quality);
    } catch {
      reject(new ImageCompressionError('图片压缩失败，请重新选择图片或更换浏览器后重试。'));
    }
  });
}

/** Compress only oversized uploads; every successful compressed result is below 200 KB. */
export async function prepareImageForUpload(file: File): Promise<File> {
  const extension = file.name.match(/\.([^.]+)$/)?.[1]?.toLowerCase();
  const extensionType = extension ? MIME_BY_EXTENSION[extension] : undefined;
  const sourceType = file.type || extensionType;
  if (!sourceType || !SUPPORTED_TYPES.has(sourceType) || (file.type && extensionType && file.type !== extensionType)) {
    throw new ImageCompressionError('请选择 JPG、PNG 或 WebP 图片。');
  }
  if (file.size <= MAX_IMAGE_UPLOAD_BYTES) {
    return file.type ? file : new File([file], file.name, { type: sourceType, lastModified: file.lastModified });
  }

  let objectUrl: string | undefined;
  let canvas: HTMLCanvasElement | undefined;
  try {
    objectUrl = URL.createObjectURL(file);
    const image = await loadImage(objectUrl);
    canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) throw new ImageCompressionError('当前浏览器无法压缩图片，请更换浏览器后重试。');

    const originalEdge = Math.max(image.naturalWidth, image.naturalHeight);
    let edge = Math.min(originalEdge, 1600);
    let type: 'image/webp' | 'image/jpeg' = 'image/webp';

    const draw = () => {
      context.clearRect(0, 0, canvas!.width, canvas!.height);
      if (type === 'image/jpeg') {
        context.fillStyle = '#fff';
        context.fillRect(0, 0, canvas!.width, canvas!.height);
      }
      // Browser image decoding handles the source orientation before drawing.
      context.drawImage(image, 0, 0, canvas!.width, canvas!.height);
    };

    while (true) {
      canvas.width = Math.max(1, Math.round(image.naturalWidth * edge / originalEdge));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * edge / originalEdge));
      draw();

      for (const quality of QUALITIES) {
        let blob = await encode(canvas, type, quality);
        if (type === 'image/webp' && (!blob || blob.type !== 'image/webp')) {
          // Unsupported canvas encoders may silently produce PNG instead of WebP.
          type = 'image/jpeg';
          draw();
          blob = await encode(canvas, type, quality);
        }
        if (!blob || !blob.size || blob.type !== type) {
          throw new ImageCompressionError('图片压缩失败，请重新选择图片或更换浏览器后重试。');
        }
        if (blob.size < MAX_IMAGE_UPLOAD_BYTES) {
          const baseName = file.name.replace(/\.[^.]*$/, '') || 'image';
          const extension = type === 'image/webp' ? 'webp' : 'jpg';
          return new File([blob], `${baseName}.${extension}`, { type, lastModified: file.lastModified });
        }
      }

      if (edge <= 64) break;
      edge = Math.max(64, Math.floor(edge * 0.75));
    }
    throw new ImageCompressionError('无法将图片压缩到 200 KB 以下，请换一张图片后重试。');
  } catch (error) {
    if (error instanceof ImageCompressionError) throw error;
    throw new ImageCompressionError('图片压缩失败，请重新选择图片或更换浏览器后重试。');
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }
}
